"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Separator } from "@/components/ui/separator";
const explanations = ["Each step is checked against the mark scheme. Setura follows the student's method line by line and matches it to the criteria an examiner would award.", "The lost mark gets a cause. Setura separates a knowledge gap (the student didn't know the content) from a process error (they knew it and slipped applying it). The fix for each is different.", "Teachers see the pattern, not just the score. Across a class, Setura shows which criteria students keep dropping, so intervention goes where it's needed."];
export default function HowItWorks() {
  const {
    ref: headingRef,
    isVisible: isHeadingVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: boxRef,
    isVisible: isBoxVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: listRef,
    isVisible: isListVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section data-section-id="3290" id="how-it-works" className="bg-background text-foreground py-20 sm:py-24 md:py-32 px-6 md:px-12 lg:px-20">
      <div className="mx-auto max-w-7xl">
        <h2 ref={headingRef} className={`font-sans text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-foreground mb-12 md:mb-16 max-w-3xl transition-all duration-1000 ease-out ${isHeadingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          How Setura reads a student&apos;s working.
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-stretch">
          {/* Left Column: Placeholder Box */}
          <div ref={boxRef} className={`lg:col-span-6 w-full flex transition-all duration-1000 ease-out ${isBoxVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative w-full min-h-[280px] sm:min-h-[360px] lg:min-h-[440px] border border-border bg-card/30 flex items-center justify-center p-8 text-center rounded-sm overflow-hidden hover:-translate-y-1 hover:scale-[1.01] transition-transform duration-300 !bg-[url(https://resseti.com/wp-content/uploads/2026/09/05-feature-diagnosis.webp)] bg-contain bg-center bg-no-repeat">
              {/* Fine grid pattern */}
              <div className="absolute inset-0 pointer-events-none opacity-50" style={{
              backgroundImage: `
                    linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
                    linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
                  `,
              backgroundSize: "32px 32px"
            }} />

              {/* Corner accent marks */}
              <div className="absolute top-2 left-2 size-3 border-t border-l border-[#FFDB99] pointer-events-none" />
              <div className="absolute top-2 right-2 size-3 border-t border-r border-[#FFDB99] pointer-events-none" />
              <div className="absolute bottom-2 left-2 size-3 border-b border-l border-[#FFDB99] pointer-events-none" />
              <div className="absolute bottom-2 right-2 size-3 border-b border-r border-[#FFDB99] pointer-events-none" />

              
            </div>
          </div>

          {/* Right Column: Three Paragraphs separated by thin lines */}
          <div ref={listRef} className="lg:col-span-6 flex flex-col justify-center">
            {explanations.map((text, i) => <div key={i} data-index={i} style={{
            transitionDelay: `${i * 150}ms`
          }} className={`flex flex-col transition-all duration-700 ease-out ${isListVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                {i > 0 && <Separator className="my-8 md:my-10 bg-border" />}
                <div className="flex items-start">
                  <span className="w-10 sm:w-12 shrink-0 font-mono text-sm sm:text-base font-semibold uppercase tracking-wide text-[#FFDB99]">
                    {i + 1}
                  </span>
                  <p className="flex-1 font-sans text-base sm:text-lg leading-relaxed text-muted-foreground">
                    {text}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}